import { useBlocker, useRouter, useSearch } from '@tanstack/react-router';
import { RouteType, useCreateOrPublishMultipleShipments } from '@uturn/api/v2';
import { Page } from '@uturn/portal/components';
import Overlay from '@uturn/portal/components/overlay';
import { useFinancialBlock } from '@uturn/portal/context/financial-block-context';
import {
	bulkCheckContainerNumbersInUse,
	useLocationDatesValidation,
} from '@uturn/portal/modules/shipments/validation';
import type { TransportTypeCode } from '@uturn/portal/types/shipment';
import { getApiErrorMessage } from '@uturn/portal/utils';
import type { AgGridReact } from '@uturn/ui-kit';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	Button,
	Input,
	Separator,
	sonner,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@uturn/ui-kit';
import dayjs from 'dayjs';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { CreditLimitPublishBlockedModal } from '../../components/credit-limit-publish-blocked-modal';
import MultiCreateTable from '../../components/multi-create-table';
import {
	schema,
	targetPriceSchema,
	transformBack,
} from '../../components/multi-create-table/types/schema';
import { toLocationActionsFromRow } from '../../components/multi-create-table/utils/locations';
import { containerNumberNonSOWNSchema } from '../../schema/container';
import { MINIMUM_REALISTIC_PRICE } from '../../validation/price';

const FF_GROUP_NAME = 'shipment_bulk_create_group_name';

const arrayifyLocations = (data: any) => {
	return Object.entries(data).reduce(
		(acc, [key, value]) => {
			// Check if the key starts with "location_" and is followed by a number
			const match = key.match(/^location_(\d+)_(.+)$/);

			if (match) {
				// Extract the numeric part and the property name
				const [, index, property] = match;

				// Ensure the correct index in the array (convert index to 0-based)
				const locationIndex = parseInt(index, 10) - 1;
				if (!acc.locations[locationIndex]) {
					acc.locations[locationIndex] = {};
				}

				// Assign the value to the corresponding property in the grouped object
				acc.locations[locationIndex][property] = value;
			} else {
				// If the key does not match the "location_" pattern, add it directly to acc
				acc[key] = value;
			}

			return acc;
		},
		{ locations: [] },
	);
};

const Bulk = () => {
	const [groupName, setGroupName] = useState<string>('');
	const search = useSearch({
		from: '/_protected/shipments/add/bulk',
	});
	const { t } = useTranslation();
	const router = useRouter();
	const { mutate, isPending } = useCreateOrPublishMultipleShipments();

	const [
		creditLimitPublishBlockedModalOpen,
		setCreditLimitPublishBlockedModalOpen,
	] = useState(false);

	const groupNameEnabled = useFeatureFlagEnabled(FF_GROUP_NAME);
	const { validateLocationDates } = useLocationDatesValidation();
	const ref = useRef<AgGridReact>(null);
	const blocker = useBlocker({
		shouldBlockFn: (props) => {
			// Temporary fix until we move to a proper modal after succesfull publish
			if (props.action === 'REPLACE') {
				return false;
			}

			// TODO: When intregating schemas and the correct types, this needs to be typed properly a swell
			const data = [];
			ref.current?.api.forEachNode((node) => data.push(node.data));
			const isDirty = data.some((row) => row.dirty);

			return isDirty && props.current.pathname !== props.next.pathname;
		},
		withResolver: true,
	});

	const { isFinancialBlocked } = useFinancialBlock();

	const onClickHandler = async (publish = true) => {
		const data = [];
		let hasError = false;

		ref.current?.api.forEachNode((node) => {
			if (node.data?.dirty) {
				const transformedData = transformBack(node.data);
				const transformedDataWithArrifiedLocations =
					arrayifyLocations(transformedData);

				const { success, error } = schema.safeParse(
					transformedDataWithArrifiedLocations,
				);
				const cellErrors: Record<
					string,
					{ isValid: false; errorMessage: string }
				> = {};

				if (!success) {
					error.issues.forEach(({ path, message }) => {
						// TODO: revisit this, this is not future proof
						let index = path[0] as string | number;
						if (path[0] === 'locations') {
							index = `location_${(path[1] as number) + 1}_${path[2]}`;
						}
						const key = String(index);
						if (!cellErrors[key]) {
							cellErrors[key] = { isValid: false, errorMessage: message };
						}
					});
				}

				// SOWN-aware 4th-char rule. Runs outside the schema because the schema's
				// root-level superRefine doesn't fire while other required fields are
				// still empty (ZodEffects only runs when the inner object parse succeeds).
				// Mirrors manual's useContainerNumberValidation.
				const containerNumber =
					transformedDataWithArrifiedLocations.containerNumber;
				const scacCode =
					transformedDataWithArrifiedLocations.shippingLine?.scacCode;
				if (
					typeof containerNumber === 'string' &&
					containerNumber.trim() !== '' &&
					scacCode !== 'SOWN'
				) {
					const result =
						containerNumberNonSOWNSchema.safeParse(containerNumber);
					if (!result.success && !cellErrors.containerNumber) {
						cellErrors.containerNumber = {
							isValid: false,
							errorMessage: result.error.issues[0].message,
						};
					}
				}

				const dateValidationResults = validateLocationDates(
					toLocationActionsFromRow(node.data),
					'',
					publish,
					true,
				);

				if (!dateValidationResults.valid) {
					dateValidationResults.issues.forEach(({ path, message }) => {
						const pathMatch = String(path).match(
							/^locations\.(\d+)\.action\.(dateFrom|dateUntil)$/,
						);
						if (!pathMatch) {
							return;
						}
						const [, locationIndex, dateField] = pathMatch;
						const cellField = dateField === 'dateFrom' ? 'from' : 'until';
						const cellId = `location_${Number(locationIndex) + 1}_${cellField}`;
						if (!cellErrors[cellId]) {
							cellErrors[cellId] = { isValid: false, errorMessage: message };
						}
					});
				}

				// Target price must be realistic when publishing (mirrors manual create).
				const parsedPrice = targetPriceSchema.safeParse(transformedData);
				const targetPrice = parsedPrice.success
					? parsedPrice.data.price.quantity
					: undefined;
				if (
					publish &&
					(targetPrice ?? 0) < MINIMUM_REALISTIC_PRICE &&
					!cellErrors.price
				) {
					cellErrors.price = {
						isValid: false,
						errorMessage: t(
							'formValidation:price_too_low',
							'Please enter a realistic target price.',
						),
					};
				}

				if (Object.keys(cellErrors).length === 0) {
					data.push(transformedDataWithArrifiedLocations);
					return;
				}

				hasError = true;

				const result = Object.entries(cellErrors).reduce<
					Record<string, unknown>
				>((acc, [key, errorEntry]) => {
					acc[key] = { ...node.data[key], ...errorEntry };
					return acc;
				}, {});

				ref.current?.api.applyTransaction({
					update: [
						{
							...node.data,
							...result,
						},
					],
				});
			}
		});

		if (hasError) {
			sonner.error(
				t('toast.error-title', 'Uh oh! Something went wrong.').toString(),
				{
					description: t(
						'general.required-fields',
						'One or more fields have errors, please correct them before submitting',
					),
				},
			);

			ref.current?.api.redrawRows();
			return;
		}

		if (data.length > 0) {
			// Non-blocking: warn about container numbers already in use, but still
			// allow creation to proceed (mirrors manual create). Checked in a single bulk request
			const containerNumbers = data
				.map((row) => row.containerNumber)
				.filter(
					(containerNumber): containerNumber is string =>
						typeof containerNumber === 'string',
				);

			const inUseContainerNumbers =
				await bulkCheckContainerNumbersInUse(containerNumbers);

			// Persistent so it survives the success toast and redirect.
			if (inUseContainerNumbers.length > 0) {
				sonner.warning(
					t(
						'pages.bulk_create_shipments.container_in_use',
						'The following containers are already in use: {{containerNumbers}}',
						{ containerNumbers: inUseContainerNumbers.join(', ') },
					),
					{ duration: Infinity },
				);
			}

			mutate(
				{
					data: data.map((row) => ({
						cargoDescription: row.cargoDescription,
						concept: !publish,
						conditions: row.termsAndConditions.label, // TODO: fix this
						equipment: {
							containerNumber: row.containerNumber,
							isoType: row.isoCode,
							shipmentUnitId:
								row.containerType && Number(row.containerType.value),
						},
						fixedPrice: !row.bidOnly,
						generatorSet: row.generatorSet,
						grossWeight: {
							quantity: row.cargoGrossWeight.quantity,
							unit: row.cargoGrossWeight.unit,
						},
						locationActions: row.locations.map((location, index) => ({
							dateFrom: dayjs(location.from).tz().format(),
							dateUntil: dayjs(location.until).tz().format(),
							location: {
								...(location.location.id
									? { id: location.location.id }
									: { googlePlaceId: location.location.placeId }),
							},
							locationActionType: location.type.value
								.replace('-', '')
								.toUpperCase(), // TODO: fix this
							port: location.port,
							reference: location.reference,
							sequenceNumber: index + 1,
							shippingLineId:
								location.shippingLine && Number(location.shippingLine.value),
							vesselName: location.vesselName,
						})),
						numberOfCopiesToBeCreated: 1,
						preferredCarriersIds: row.preferredCarriers,
						price: {
							quantity: row.price.quantity,
							currency: row.price.currency,
						},
						publishUntilDate: dayjs(row.locations[0].until).tz().format(),
						requirementIds: row.requirements,
						routeKpis: [
							{
								isSelected: true,
								routeType: RouteType.TRUCK,
							},
						],
						seal: row.seal,
						shipperReference: row.invoiceReference,
						shippingLineId: row.shippingLine && Number(row.shippingLine.value),
						unCode: row.unCode,
						dutiesPaid: row.vat ?? false,
						...(groupName !== '' && { newGroup: { name: groupName } }),
					})),
				},
				{
					onSuccess: () => {
						sonner.success(
							t(
								`pages.bulk_create_shipments.title.message.success`,
								'Your shipment(s) have been successfully created',
							),
						);

						if (groupName !== '') {
							router.navigate({ to: '/shipments/groups', replace: true });
							return;
						}
						if (publish) {
							router.navigate({ to: '/shipments', replace: true });
							return;
						}
						router.navigate({ to: '/shipments/drafted', replace: true });
					},
					onError: (error) => {
						const message = getApiErrorMessage(error);
						if (message === 'CREDIT_LIMIT_EXCEEDED') {
							setCreditLimitPublishBlockedModalOpen(true);
							sonner.error(
								t(
									'pages.publish-shipment.api-error.credit-limit-exceeded',
									'Credit limit exceeded',
								),
							);
						} else {
							sonner.error(
								message ??
									t(
										'toast.error-title',
										'Uh oh! Something went wrong.',
									).toString(),
								{
									description: t(
										'general.try-again-later',
										'An unknown error occured, please contact customer service',
									),
								},
							);
						}
					},
				},
			);
		}
	};

	const transportType: TransportTypeCode | undefined = search.transportType;

	return (
		<>
			<Helmet
				title={t(
					'pages.bulk_create_shipments.title',
					'Bulk create shipments',
				).toString()}
			/>
			{isPending && (
				<Overlay>{t('general.loading', 'Loading...').toString()}</Overlay>
			)}
			<Page
				title={t(
					'pages.bulk_create_shipments.title',
					'Bulk create shipments',
				).toString()}
				backButton={{
					label: t(
						'pages.bulk_create_shipments.back_button',
						'Add new shipment',
					),
					href: '/shipments/add',
				}}
				stickyBar={{
					position: 'bottom',
					children: (
						<div className="flex items-center justify-end gap-5">
							{groupNameEnabled && (
								<>
									<Input
										className="max-w-[300px]"
										placeholder="Group name (optional)"
										value={groupName}
										onChange={(e) => setGroupName(e.target.value)}
									/>
									<Separator orientation="vertical" className="h-5" />
								</>
							)}
							<Button
								variant="link"
								className="p-0"
								onClick={() => onClickHandler(false)}
							>
								{t('shipments.bulk.concept', 'Save as concept')}
							</Button>
							{isFinancialBlocked ? (
								<TooltipProvider delayDuration={10}>
									<Tooltip>
										<TooltipTrigger asChild>
											<Button
												className="min-w-[120px] cursor-not-allowed opacity-50"
												onClick={undefined}
											>
												{t('shipments.bulk.publish', 'Publish')}
											</Button>
										</TooltipTrigger>
										<TooltipContent side="top" align="center" sideOffset={1}>
											<div className="max-w-[450px] text-red-500">
												{t(
													'pages.create-shipment.financially-block.description',
													'You are unable to publish shipments due to financial restrictions. Save the shipment as a Concept.',
												)}
											</div>
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							) : (
								<Button
									className="min-w-[120px]"
									onClick={() => onClickHandler(true)}
								>
									{t('shipments.bulk.publish', 'Publish')}
								</Button>
							)}
						</div>
					),
				}}
			>
				<MultiCreateTable ref={ref} transportType={transportType} />
				{blocker.status === 'blocked' && (
					<AlertDialog open={true}>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{t(
										'dialog.blocker.title',
										'Are you sure you want to leave this page?',
									)}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{t(
										'dialog.blocker.description',
										'You have unsaved changes. Are you sure you want to leave this page? Any changes you have made will be lost.',
									)}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel onClick={() => blocker.reset()}>
									{t('dialog.blocker.cancel-button', 'Stay')}
								</AlertDialogCancel>
								<AlertDialogAction onClick={() => blocker.proceed()}>
									{t('dialog.blocker.confirm-button', 'Leave')}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				)}
				<CreditLimitPublishBlockedModal
					open={creditLimitPublishBlockedModalOpen}
					onSubmit={() => {
						setCreditLimitPublishBlockedModalOpen(false);
						onClickHandler(false);
					}}
					onCancel={() => {
						setCreditLimitPublishBlockedModalOpen(false);
					}}
				/>
			</Page>
		</>
	);
};

export default Bulk;
